// 汎用 CSV パーサ + 文字コード自動判定（ブラウザ環境）

import type { Carrier } from "../../types";

export type DetectResult = {
  encoding: "UTF-8" | "UTF-8 with BOM" | "Shift_JIS";
  text: string;
  carrier: Carrier | "unknown";
};

// バイト配列から文字コード判定 → デコード
export function decodeBytes(buf: ArrayBuffer): { encoding: DetectResult["encoding"]; text: string } {
  const bytes = new Uint8Array(buf);

  // BOM チェック (EF BB BF)
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    const text = new TextDecoder("utf-8").decode(bytes.subarray(3));
    return { encoding: "UTF-8 with BOM", text };
  }

  // UTF-8 strict トライ
  try {
    const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return { encoding: "UTF-8", text };
  } catch {
    // Shift_JIS フォールバック
    const text = new TextDecoder("shift_jis").decode(bytes);
    return { encoding: "Shift_JIS", text };
  }
}

// ヘッダー行を見てキャリア判定
export function detectCarrier(headerLine: string): Carrier | "unknown" {
  // 佐川：「集配日」「担当者コード」「便種コード」が並ぶ
  if (headerLine.includes("集配日") && headerLine.includes("担当者コード") && headerLine.includes("便種コード")) {
    return "sagawa";
  }
  // ヤマト：「包括契約ID」「委託社員番号」「稼働日」
  if (headerLine.includes("委託社員番号") && headerLine.includes("稼働日")) {
    return "yamato";
  }
  return "unknown";
}

// RFC4180 風 CSV パーサ（CRLF / LF / クォート内カンマ・改行対応）
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let cur: string[] = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        cur.push(cell);
        cell = "";
      } else if (ch === "\r") {
        // 次が \n のとき行終端、それ以外でも行終端扱いにする
      } else if (ch === "\n") {
        cur.push(cell);
        cell = "";
        // 末尾の空行は捨てる
        if (!(cur.length === 1 && cur[0] === "")) rows.push(cur);
        cur = [];
      } else {
        cell += ch;
      }
    }
  }
  if (cell !== "" || cur.length > 0) {
    cur.push(cell);
    if (!(cur.length === 1 && cur[0] === "")) rows.push(cur);
  }
  return rows;
}

// 入口：File オブジェクトから判定 + パース
export async function readCsvFile(file: File): Promise<{ encoding: DetectResult["encoding"]; carrier: Carrier | "unknown"; rows: string[][] }> {
  const buf = await file.arrayBuffer();
  const { encoding, text } = decodeBytes(buf);
  const firstNl = text.indexOf("\n");
  const headerLine = firstNl < 0 ? text : text.substring(0, firstNl);
  const carrier = detectCarrier(headerLine);
  const rows = parseCsv(text);
  return { encoding, carrier, rows };
}
